import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCircle2, Coins, XCircle } from 'lucide-react';
import { useAppStore } from '@/store';

function formatTimeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const typeIcon: Record<string, { Icon: typeof Bell; color: string }> = {
  TRANSACTION_APPROVED: { Icon: CheckCircle2, color: 'text-green-600' },
  TRANSACTION_REJECTED: { Icon: XCircle, color: 'text-red-600' },
  TRANSACTION_SUBMITTED: { Icon: Coins, color: 'text-orange-600' },
};

interface NotificationBellProps {
  buttonClassName?: string;
  align?: 'left' | 'right';
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  buttonClassName,
  align = 'right',
}) => {
  const navigate = useNavigate();
  const {
    notifications,
    unreadNotifications,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
  } = useAppStore();
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    fetchNotifications().catch(console.error);
    const interval = setInterval(() => {
      fetchNotifications().catch(console.error);
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      if (next) fetchNotifications().catch(console.error);
      return next;
    });
  };

  const handleNotificationClick = async (id: number, isRead: boolean) => {
    if (!isRead) await markNotificationRead(id);
    setOpen(false);
    navigate('/billing');
  };

  const hasUnread = unreadNotifications > 0;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleOpen}
        className={
          buttonClassName ||
          'relative flex h-10 w-10 items-center justify-center rounded-2xl border border-white/70 bg-white/80 text-slate-600 shadow-sm transition-colors hover:bg-white'
        }
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell className="h-5 w-5" />
        {hasUnread && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {unreadNotifications > 99 ? '99+' : unreadNotifications}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadNotifications > 0 && (
              <button
                onClick={() => markAllNotificationsRead()}
                className="flex items-center gap-1 text-xs font-medium text-orange-600 hover:text-orange-700"
              >
                <Check className="h-3.5 w-3.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell className="mx-auto h-8 w-8 text-gray-300" />
                <p className="mt-2 text-sm text-gray-500">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => {
                const meta = typeIcon[n.notification_type] || typeIcon.TRANSACTION_SUBMITTED;
                const Icon = meta.Icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n.id, n.is_read)}
                    className={`flex w-full gap-3 border-b border-gray-50 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${
                      n.is_read ? '' : 'bg-orange-50/40'
                    }`}
                  >
                    <div className={`mt-0.5 shrink-0 ${meta.color}`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${n.is_read ? 'font-medium text-gray-700' : 'font-semibold text-gray-900'}`}>
                        {n.title}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500 break-words">{n.message}</p>
                      <p className="mt-1 text-[10px] text-gray-400">{formatTimeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-orange-500" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
